<template>
  <!-- R6: tela cheia no celular, dock "Voltar | Salvar"; Excluir desce para o corpo. -->
  <DashboardModalShell
    :open="showModal"
    :full-screen="mobileShell"
    title="Editar plantão"
    :meta="`#${infos.stationId}`"
    test-id="update-station-panel"
    @close="$emit('closeModal')"
    :hasFooterBorder="true"
    :hasHeaderBorder="true"
  >
    <template #header>
      <div class="flex items-center gap-3 w-full mb-1">
        <StationIdBadge
          :stationId="infos.stationId"
          :doctorStationId="props.infos?.doctorStation?.id"
        />

        <span class="font-bold text-lg md:text-2xl text-textPrimary">Editar plantão</span>
      </div>
    </template>

    <div class="mb-6">
      <StatusSummaryBadge :status="props.exceptionStatus" />
    </div>

    <!-- E-06 / Q31: ocupado sem identificação edita horário/valor mantendo estado e responsável. -->
    <div v-if="isUnidentifiedStation" class="text-left mb-4 md:max-w-sm">
      <UnidentifiedCoverageCard compact :responsible-name="props.infos.coverageResponsibleName" />
    </div>

    <div v-else-if="!props.infos.doctorStation?.doctor" class="text-left">
      <span class="text-base rounded py-1 px-2 bg-alert/10 border border-alert"> Aberta </span>
      <!-- H6: o PUT exige médico; na vaga aberta só setor/especialidade vão (lote com 1 id). -->
      <p
        v-if="isOpenStation"
        class="mt-3 mb-4 px-3 py-2 rounded-md border border-[#F18F1B] bg-[#FEF3E7] text-sm text-textPrimary"
        data-testid="update-station-open-station-notice"
      >
        {{ OPEN_STATION_NOTICE }}
      </p>
    </div>

    <div v-else class="flex flex-col md:flex-row gap-4 md:gap-6 mb-4 md:mb-6">
      <CardDutyWithDoctor
        data-testid="update-station-doctor-input"
        :formation="props.infos?.doctorStation?.dsPaymentDoctorFormation?.name || ''"
        :team="props.infos?.doctorStation?.ourTeam ? 'Interno' : 'Externo'"
        :position="props.infos?.position?.name"
        :shift="props.infos?.shift?.name || ''"
        :dutyLabel="props.infos?.doctorStation?.type"
        :doctorName="props.infos?.doctorStation?.doctor?.mainCrm?.name"
        :doctorCrm="`${props.infos?.doctorStation?.doctor?.mainCrm?.number || ''}-${
          props.infos?.doctorStation?.doctor?.mainCrm?.uf || ''
        }`"
        :active="false"
        :scheduleId="cardScheduleId"
        :npsScore="props.infos?.doctorNpsScore?.npsScore"
        :avatarUrl="props.infos?.doctorStation?.doctor?.user?.avatarUrl"
      >
      </CardDutyWithDoctor>

      <ValueDetailsSection
        :original-value="props.infos?.value"
        :approved-value="props.infos?.value"
        :request-antecipe="props.infos.doctorStation?.requestAntecipe"
        :antecipe-was-paid="props.infos.doctorStation?.antecipeWasPaid"
        :is-exception-mode="false"
        :approval-status="'Approved'"
      />
    </div>

    <div class="flex flex-col w-full" :class="mobileShell ? '' : 'max-h-[70vh]'">
      <div class="grid gap-3" :class="mobileShell ? 'grid-cols-1' : 'grid-cols-2'">
        <div class="station-form-category-container mb-6 text-left">
          <span class="station-form-category-title">Turno</span>
          {{ form.shift.id > 0 ? form.shift?.name : 'Não informado' }}
        </div>
        <div class="station-form-category-container mb-6 text-left">
          <span class="station-form-category-title">Regra de proporcionalidade</span>
          <p class="text-sm">{{ scheduleProportionalityRule }}</p>
        </div>
      </div>

      <!-- H6: setor/especialidade são do plantão (P1) e nunca são sugeridos no Editar (P2). -->
      <SectorSpecialtyFields
        v-if="flags.enabledDashboardV2"
        class="mb-6"
        :health-unit-id="healthUnitIdForSector"
        :company-id="props.infos.companyId || null"
        :sector="form.sector"
        :specialty="form.specialty"
        :summary="sectorSpecialtyPreview"
        :description="`Vale só para este plantão (#${props.infos.stationId}). Os outros plantões desta posição e desta escala não mudam.`"
        @update:sector="form.sector = $event"
        @update:specialty="form.specialty = $event"
      />
      <p
        v-if="flags.enabledDashboardV2 && !healthUnitIdForSector"
        class="-mt-4 mb-6 text-sm text-textSecondary text-left"
        data-testid="update-station-sector-no-unit"
      >
        Unidade não identificada para listar setores.
      </p>

      <!-- Vaga aberta: horário e valor não são salvos por aqui — ficam travados (inert).
           Perna travada (legLock, backend #3055): idem — horário e valor não podem mudar,
           mas setor/especialidade e o restante seguem salváveis pelo mesmo botão.
           `flex flex-col` repete o contêiner pai: sem colapso de margens, o layout é o da base. -->
      <div
        class="flex flex-col"
        :inert="areScheduleFieldsLocked || undefined"
        :aria-disabled="areScheduleFieldsLocked || undefined"
        :class="{ 'opacity-50 pointer-events-none select-none': areScheduleFieldsLocked }"
        data-testid="update-station-schedule-fields"
      >
        <HourAndDateForm
          v-model:date="form.date"
          v-model:timezone="form.timezone"
          v-model:start-hour="form.startHour"
          v-model:end-hour="form.endHour"
          v-model:endDate="form.endDate"
          v-model:finishedAtUntil="form.finishedAtUntil"
          :is-update-mode="true"
          @validation-status="handleValidation"
        />
        <div
          v-if="dutyFeeCards.length > 0"
          class="station-form-category-container mb-6 mt-4 text-left"
        >
          <span class="station-form-category-title">Valores do plantão</span>
        </div>

        <div class="flex flex-wrap gap-3 md:gap-4 justify-start mb-4 md:mb-6">
          <CardDutyFee
            v-for="(card, index) in dutyFeeCards"
            :key="index"
            :formation="card.formation"
            :value="card.value"
            :repass-mode="card.repassMode"
            :active="activeIndex === index"
            @click="setActiveIndex(index)"
          />
        </div>
      </div>

      <DoctorChecks
        @saveChecks="updateChecks"
        @editModeChanged="handleEditModeChanged"
        @hasPendingChanges="handleChildChanges"
        @navigateToLinkedStation="handleNavigateToLinkedStation"
        v-if="infos.doctorId && canManageEvidences && !isUnidentifiedStation"
        :station-id="infos.stationId"
        :infos="form.histories"
        :manual-check-block-enabled="manualCheckBlockEnabled"
        :timezone="form.timezone"
        :health-unit-latitude="infos?.healthUnitLatitude"
        :health-unit-longitude="infos?.healthUnitLongitude"
      />

      <!-- Tentativas de check (aceitas e rejeitadas) do plantão. Mesma linguagem
           visual do "Registro de plantão" logo acima. A montagem é preguiçosa
           (`v-if`, nunca `v-show`): o painel busca na API ao montar, e ninguém
           deve pagar essa chamada só por abrir a edição do plantão. Sem modal
           sobre modal — a tabela e o mapa abrem aqui mesmo. -->
      <div v-if="infos.stationId" class="mt-6 text-left" data-testid="station-check-attempts-block">
        <div class="flex flex-row items-center justify-between mb-1 flex-wrap gap-2">
          <span class="font-bold text-black">Tentativas de check</span>
          <div
            class="flex items-center justify-center cursor-pointer border border-primary30 rounded-md py-1 px-3"
            data-testid="station-check-attempts-toggle"
            @click="showCheckAttempts = !showCheckAttempts"
          >
            <button type="button" class="flex gap-2 md:gap-4 items-center text-primary30">
              <span class="font-bold text-sm">{{
                showCheckAttempts ? 'Ocultar' : 'Ver tentativas'
              }}</span>
              <SVGIcon :path="mdiMapMarkerRadiusOutline" class="w-4 h-4 shrink-0" />
            </button>
          </div>
        </div>
        <div
          v-if="showCheckAttempts"
          class="flex flex-col w-full rounded-lg p-2 border-[2px] border-primary mb-4"
          data-testid="station-check-attempts"
        >
          <CheckAttemptsPanel :source="checkAttemptsSource" compact />
        </div>
      </div>

      <ObservationForm
        v-if="flags.enabledObservationsSystem"
        :doctorStationId="infos?.doctorStation?.id"
        :scheduleId="infos?.scheduleId"
        :timezone="infos.timezone"
      />

      <!-- Trava do Pós-Plantão (RN-07 / RF-12). Mesmo texto e mesma iconografia
           do banner do card no detalhe do plantão: cadeado `mdiLock` em cor
           institucional, nunca o `mdiLockOpenVariant` vermelho, que neste modal
           significa excepcionalidade. O bloqueio efetivo está em
           `isSaveDisabled` e no botão de excluir — banner e disable são
           desacoplados, alimentados pela mesma fonte, como no
           `ConsecutiveHoursBlockAlert`. -->
      <div
        v-if="isPostStationLocked"
        class="flex items-start gap-2 px-3 py-2 mt-6 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary text-left"
        data-testid="update-station-post-station-lock-banner"
      >
        <SVGIcon :path="mdiLock" class="w-4 h-4 mt-0.5 shrink-0 text-primary30" />
        <span>{{ POST_STATION_LOCK_BANNER_TEXT }}</span>
      </div>

      <!-- Trava por perna (backend #3055): mesmo padrão visual do banner acima, com o
           motivo pronto do backend (`legLock.reason`). A regra é do backend; aqui só
           se desabilita e explica. -->
      <div
        v-if="isLegLocked"
        class="flex items-start gap-2 px-3 py-2 mt-6 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary text-left"
        data-testid="update-station-leg-lock-banner"
      >
        <SVGIcon :path="mdiLock" class="w-4 h-4 mt-0.5 shrink-0 text-primary30" />
        <span>{{ legLockReason }}</span>
      </div>

      <ConsecutiveHoursBlockAlert :violations="consecutiveHoursViolations" class="mt-6" />

      <ScheduleOverlapAlert :alerts="overlapAlerts" mode="update" class="mt-6" />

      <!-- Celular: o dock tem só Voltar | Salvar; excluir continua a um toque, no fim
           do corpo, com a mesma trava (desabilitado, nunca escondido). -->
      <GhostButton
        v-if="mobileShell && canDeleteStation"
        data-testid="update-station-delete-button"
        class="!text-red-500 hover:!text-red-500 hover:!bg-red-100 border border-red-500 w-full mt-6"
        :loading="isExcludingLoading"
        :disabled="isDeleteLocked"
        @click="showModalDelete = true"
        text="Excluir plantão"
        height="48px"
        width=""
      />
    </div>

    <template #footer>
      <div class="flex flex-col-reverse md:flex-row gap-3 md:gap-5 justify-between">
        <div class="w-full md:w-auto">
          <!-- Desabilitado, não escondido: o banner explica o porquê, e um botão
               que desaparece deixaria o usuário procurando a ação. -->
          <GhostButton
            v-if="canDeleteStation"
            data-testid="update-station-delete-button"
            class="!text-red-500 hover:!text-red-500 hover:!bg-red-100 border border-red-500 w-full md:w-auto"
            :loading="isExcludingLoading"
            :disabled="isDeleteLocked"
            @click="showModalDelete = true"
            text="Excluir plantão"
            height="48px"
            width=""
          />
        </div>
        <div class="flex flex-row items-center gap-2 md:gap-4 w-full md:w-auto">
          <!-- Conflito de agenda: a edicao JA foi gravada, entao nao ha o que salvar de novo. -->
          <filled-button
            v-if="hasOverlapAlerts"
            data-testid="update-station-overlap-ack-button"
            @click="$emit('saved')"
            text="Entendi, fechar"
            height="48px"
            class="!bg-[#337C88] flex-1 md:flex-none md:w-[180px]"
          />
          <template v-else>
            <OutlinedButton @click="$emit('goBack')" text="Voltar" height="48px" width="74px" />
            <filled-button
              data-testid="update-station-submit"
              :loading="isSavingLoading"
              :disabled="isSaveDisabled"
              @click="handleSubmit"
              text="Salvar edição"
              height="48px"
              class="!bg-[#337C88] flex-1 md:flex-none md:w-[180px]"
            />
          </template>
        </div>
      </div>
    </template>

    <template #dock>
      <div class="flex gap-3">
        <div v-if="hasOverlapAlerts" class="flex-1">
          <filled-button
            data-testid="update-station-overlap-ack-button"
            class="!bg-[#337C88]"
            width="100%"
            height="44px"
            text="Entendi, fechar"
            @click="$emit('saved')"
          />
        </div>
        <template v-else>
          <div class="flex-1">
            <OutlinedButton width="100%" height="44px" text="Voltar" @click="$emit('goBack')" />
          </div>
          <div class="flex-1">
            <filled-button
              data-testid="update-station-submit"
              class="!bg-[#337C88]"
              width="100%"
              height="44px"
              text="Salvar"
              :loading="isSavingLoading"
              :disabled="isSaveDisabled"
              @click="handleSubmit"
            />
          </div>
        </template>
      </div>
    </template>
  </DashboardModalShell>

  <!-- No celular, confirmação de exclusão, aviso do médico e o
       detalhe vinculado abrem POR CIMA do painel (docs/z-layers.md). -->
  <MobileOverlayLayer :active="mobileShell">
    <modal-vue
      @close="showModalDelete = false"
      :modalActive="showModalDelete"
      maxWidth="max-w-[400px]"
    >
      <template #header>
        <p class="font-bold">Excluir Plantão</p>
      </template>
      <p class="text-base font-normal">
        Tem certeza que deseja excluir o plantão?<br />Essa ação não poderá ser desfeita!
      </p>
      <!-- acceptance-terms (D8/D54d): excluir cancela o convite/troca pendente do plantão. -->
      <p
        v-if="deletePendingNotice"
        class="mt-4 text-sm text-left bg-warning/20 border border-warning rounded-md px-4 py-2"
        data-testid="update-station-delete-pending-notice"
      >
        {{ deletePendingNotice }}
      </p>
      <div v-if="reasonFields.length" class="flex flex-col gap-4 mt-4">
        <ChangeReasonField
          v-for="(field, index) in reasonFields"
          :key="field.kind"
          v-model="reasonValues[field.kind]"
          v-bind="field"
          :show-errors="showReasonErrors"
          :scope-note="index === reasonFields.length - 1 ? reasonScopeNote : ''"
        />
      </div>
      <template #footer>
        <div class="flex justify-center gap-4">
          <OutlinedButton width="119px" @click="showModalDelete = false" text="Cancelar" />
          <filled-button
            data-testid="update-station-delete-confirm-button"
            :loading="isExcludingLoading"
            :disabled="isCheckingDeletePending"
            text="Excluir"
            width="119px"
            @click="handleDeleteStation"
            class="bg-primary30"
          />
        </div>
      </template>
    </modal-vue>

    <!-- acceptance-terms (D8/D54d, CA-11): salvar cancela o convite/troca pendente do
         plantão — o escalista é avisado ANTES e só grava se confirmar. -->
    <modal-vue
      @close="cancelPendingWarning"
      :modalActive="pendingWarning !== null"
      maxWidth="max-w-[400px]"
    >
      <template #header>
        <p class="font-bold">Salvar edição?</p>
      </template>
      <div class="text-base font-normal text-left" data-testid="update-station-pending-warning">
        <p class="text-sm bg-warning/20 border border-warning rounded-md px-4 py-2">
          {{ pendingWarning }}
        </p>
        <p class="mt-4">Deseja salvar mesmo assim?</p>
      </div>
      <template #footer>
        <div class="flex justify-center gap-4">
          <OutlinedButton
            data-testid="update-station-pending-warning-cancel"
            width="119px"
            @click="cancelPendingWarning"
            text="Voltar"
          />
          <filled-button
            data-testid="update-station-pending-warning-confirm"
            text="Salvar"
            width="119px"
            @click="confirmPendingWarning"
            class="bg-primary30"
          />
        </div>
      </template>
    </modal-vue>

    <!-- Médico "coringa" (422): sem seletor de cobertura aqui, então só "Entendi". -->
    <PlaceholderDoctorBlockedModal
      v-if="placeholderBlocked.isOpen.value"
      v-model="placeholderBlocked.isOpen.value"
      :doctor-name="placeholderBlocked.doctorName.value"
      :allow-unidentified="false"
    />
    <!-- Qualificação exigida (SPEC §4): 422 da gravação → bloqueio ou confirmação da pré-alocação. -->
    <QualificationBlockedDialog
      v-if="qualificationGuard.blocked.open"
      v-model="qualificationGuard.blocked.open"
      :doctor-name="qualificationGuard.blocked.doctorName"
      :missing="qualificationGuard.blocked.missing"
    />
    <PreAllocationConfirmDialog
      v-if="qualificationGuard.preAllocation.open"
      :model-value="qualificationGuard.preAllocation.open"
      :doctor-name="qualificationGuard.preAllocation.doctorName"
      :missing="qualificationGuard.preAllocation.missing"
      :station-count="qualificationGuard.preAllocation.affectedCount || 1"
      @confirm="qualificationGuard.confirmPreAllocation()"
      @cancel="qualificationGuard.cancelPreAllocation()"
    />

    <StationDetailsModal
      v-if="linkedStationModal.stationId !== null"
      :show-modal="linkedStationModal.show"
      :infos="{ stationId: linkedStationModal.stationId }"
      :mobile-shell="mobileShell"
      @update:showModal="linkedStationModal.show = $event"
      @closeModal="closeLinkedStationModal"
      @navigateToStation="(stationId) => (linkedStationModal = { show: true, stationId })"
    />
  </MobileOverlayLayer>
</template>

<script lang="ts" setup>
import { ref, PropType, onMounted, onBeforeUnmount, computed, reactive, watch } from 'vue'
import { mdiLock, mdiMapMarkerRadiusOutline } from '@mdi/js'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import DashboardModalShell from '@/modules/company/Dashboard/mobile/DashboardModalShell.vue'
import MobileOverlayLayer from '@/modules/company/Dashboard/mobile/MobileOverlayLayer.vue'
import HourAndDateForm from '@/modules/company/Dashboard/modals/createStation/components/form/hours.and.date.form.vue'
import ObservationForm from '@/modules/company/Dashboard/components/observation.form.vue'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StationIdBadge from '@/components/designSystem/Badges/StationIdBadge.vue'
import {
  HistoryDate,
  HistoryDateRequest,
  Observation,
  UpdateStationRequest,
  StationTypes
} from '@/Network/Types/Requests/StationRequests'
import NetworkService from '@/Network/index'
import ConsecutiveHoursBlockAlert from '@/components/designSystem/Alerts/ConsecutiveHoursBlockAlert.vue'
import ScheduleOverlapAlert from '@/components/designSystem/Alerts/ScheduleOverlapAlert.vue'
import { useConsecutiveHoursCheck } from '@/hooks/useConsecutiveHoursCheck'
import { useStationOverlapAlerts } from '@/hooks/useStationOverlapAlerts'
import type { ConsecutiveHoursCheckCandidate } from '@/Network/Types/Requests/StationRequests'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorStatus } from '@/utils/apiError'
import { useStationStore } from '@/pinia-store/useStationStore'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import {
  getHistoriesRequest,
  getStartDateAndEndDate,
  StationToUpdate,
  buildUpdateCoverageInput
} from '../createStation/helpers'
import UnidentifiedCoverageCard from '@/components/Coverage/UnidentifiedCoverageCard.vue'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import { getCoverageErrorMessage } from '@/utils/coverageErrors'
import PlaceholderDoctorBlockedModal from '@/components/Coverage/PlaceholderDoctorBlockedModal.vue'
import PreAllocationConfirmDialog from '@/components/Qualification/PreAllocationConfirmDialog.vue'
import QualificationBlockedDialog from '@/components/Qualification/QualificationBlockedDialog.vue'
import { useQualificationAllocationGuard } from '@/composables/useQualificationAllocationGuard'
import { usePlaceholderDoctorBlocked } from '@/composables/usePlaceholderDoctorBlocked'
import DoctorChecks from './components/doctor.checks.vue'
import CheckAttemptsPanel from '@/components/CheckAttempts/CheckAttemptsPanel.vue'
import StationDetailsModal from '../../components/stationDetailsModal.vue'
import CardDutyFee from '@/components/designSystem/Cards/CardDutyFee.vue'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import StatusSummaryBadge from '@/components/designSystem/Badges/StatusSummaryBadge.vue'
import { ExceptionSummaryStatus } from '@/Network/Types/Responses/StationResponses'
import { getProportionalityRuleTitle } from '@/utils/schedule'
import CardDutyWithDoctor from '@/components/designSystem/Cards/CardDutyWithDoctor.vue'
import ValueDetailsSection from '../../components/ValueDetailsSection.vue'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import SVGIcon from '@/components/SVGIcon.vue'
import { POST_STATION_LOCK_BANNER_TEXT } from '@/modules/company/PostStation/constants'
import { usePostStationLock } from '@/modules/company/PostStation/composables/usePostStationLock'
import { getPostStationApprovedMessage } from '@/modules/company/PostStation/errors'
import {
  getStationCheckInLockMessage,
  getStationDurationExceededMessage,
  isStationCheckInLocked,
  isStationDurationExceeded
} from '@/modules/company/Dashboard/modals/stationErrors'
import { useStationLegLock } from '@/modules/company/Dashboard/composables/useStationLegLock'
import { useStationPendingNotice } from '@/modules/company/Dashboard/composables/useStationPendingNotice'
import SectorSpecialtyFields from '@/components/SectorSpecialty/SectorSpecialtyFields.vue'
import type { StationSectorRef } from '@/Network/Types/Responses/StationResponses'
import {
  buildSectorSpecialtyPatch,
  hasSectorSpecialtyChanges,
  patchToBulkRequest,
  specialtyRefToSelection,
  type SpecialtySelection
} from '@/utils/sectorSpecialty'
import { catalogDisplayName } from '@/utils/stationDisplay'
import ChangeReasonField from '@/components/ChangeReason/ChangeReasonField.vue'
import { useChangeReasonForm } from '@/composables/useChangeReasonForm'
import {
  CHANGE_REASON_LABELS,
  CHANGE_REASON_TITLES,
  DELETION_REASON_OPTIONS
} from '@/utils/changeReasons'

const props = defineProps({
  showModal: { type: Boolean, default: false },
  infos: { type: Object as PropType<StationToUpdate>, required: true },
  exceptionStatus: { type: String as PropType<ExceptionSummaryStatus>, required: false },
  /** R6: tela cheia no celular. O IndexV2 liga com `isMobileLayout`. */
  mobileShell: { type: Boolean, default: false }
})

const { flags } = useFeatureFlags()
const permissionsStore = usePermissionsStore()
const dsAttendanceStore = useDsAttendanceStore()

/**
 * Q4/§4.4 — a aba de checks é somente leitura quando o bloqueio global da
 * inserção manual está ligado. A verdade vem do backend, nunca de uma variável
 * de front: preferimos o campo aditivo de `GET /stations/:id` (quando chegar) e
 * caímos em `registration-settings`, que nasce `false` — o comportamento de
 * hoje, que é também o caminho de rollback de §10.5.
 */
const manualCheckBlockEnabled = computed<boolean>(() => {
  const fromStationDetails = (props.infos as { manualCheckBlockEnabled?: boolean })
    ?.manualCheckBlockEnabled
  if (typeof fromStationDetails === 'boolean') return fromStationDetails
  return dsAttendanceStore.isManualCheckBlocked
})

const canManageEvidences = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationChecksEvidencesManaging,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const canDeleteStation = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationDeleting,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

/**
 * O `CardDutyWithDoctor` usa o `scheduleId` como recurso da checagem de
 * permissão que libera o bloco de requisitos (Formação / Equipe / Posição /
 * Turno). Sem a prop, o gate do card retorna `false` e o bloco nunca aparece.
 *
 * O fallback para `infos.schedule.id` não é defensivo à toa: os callers montam
 * `infos` à mão e o `handleEditStation` de ambos (`Dashboard/Index.vue` e
 * `Consult/ShiftChangeHistory.vue`) grava `schedule` mas NÃO regrava
 * `scheduleId`. Quando o payload de origem vem sem `station.schedule` — caso do
 * histórico de troca — sobra só `infos.schedule.id`, e ler apenas `scheduleId`
 * esconderia o bloco de quem tem a permissão.
 *
 * `undefined` (nunca `NaN`) quando não há id em nenhum dos dois, porque a prop
 * do card é `scheduleId?: number` e `NaN` passaria pelo `!props.scheduleId`.
 */
const cardScheduleId = computed<number | undefined>(() => {
  const rawScheduleId = props.infos?.scheduleId ?? props.infos?.schedule?.id
  const scheduleId = Number(rawScheduleId)
  return Number.isFinite(scheduleId) && scheduleId > 0 ? scheduleId : undefined
})

/**
 * Trava de edição do Pós-Plantão (RN-07 / RF-12): plantão aprovado tem os dados
 * críticos protegidos, e o ajuste passa obrigatoriamente pela reversão
 * justificada (US-06).
 *
 * A sondagem é feita aqui, e não recebida por prop, de propósito: este modal tem
 * DOIS callers (`Dashboard/Index.vue` e `Consult/ShiftChangeHistory.vue`), que
 * montam `infos` à mão a partir de shapes diferentes — prop-drilling exigiria
 * mapear nos dois e manter em sincronia. O status também não vem no detalhe do
 * plantão (`ShiftDetails` não tem campo de pós-plantão).
 *
 * O gate de leitura usa `cardScheduleId` (e não `infos.scheduleId` cru) pelo
 * mesmo motivo documentado nele: vindo do histórico de troca, sobra só
 * `infos.schedule.id`, e ler apenas `scheduleId` fazia o gate dar false — a
 * sondagem não acontecia e a trava preventiva não subia por lá.
 */
const {
  isPostStationLocked,
  refresh: refreshPostStationLock,
  applyApprovedError: applyPostStationApprovedError
} = usePostStationLock(
  () => props.infos?.stationId,
  () => cardScheduleId.value,
  // Só sonda plantão encerrado e com médico (a avaliação não existe antes).
  // `hasDoctor` indefinido quando o caller não montou `doctorStation`: na dúvida
  // sonda, para nunca esconder uma trava real.
  () => ({
    endHour: props.infos?.endHour,
    hasDoctor: props.infos?.doctorStation ? !!props.infos.doctorStation.doctor : undefined
  })
)
/**
 * Trava por perna (backend #3055): check-in/check-out registrado pelo app ou
 * validado na auditoria. Sondada aqui pelo mesmo motivo do Pós-Plantão acima —
 * três callers montam `infos` à mão — reaproveitando a rota de registro que já
 * existe. Travado: banner com o motivo do backend, "Excluir plantão" desabilitado
 * e horário/valor inertes. O "Salvar" continua habilitado: o backend compara por
 * diferença e aceita setor/especialidade (e checks das pernas não travadas)
 * com o restante do payload inalterado.
 */
const {
  isLegLocked,
  legLockReason,
  refresh: refreshLegLock
} = useStationLegLock(() => props.infos?.doctorStation?.id)

const isDeleteLocked = computed(() => isPostStationLocked.value || isLegLocked.value)

// `saved`: edição gravada; quem abriu o modal volta para os detalhes do plantão.
const emit = defineEmits(['closeModal', 'goBack', 'saved'])
const validationState = ref({ isValid: false, message: null as string | null })

const handleValidation = (status: { isValid: boolean; message: string | null }) => {
  validationState.value = status
}

function safeFormat(
  dateString: string | null | undefined,
  formatString: string,
  timezone: string,
  fallback = ''
): string {
  if (!dateString) {
    return fallback
  }
  try {
    const date = utcToZonedTime(dateString, timezone)
    if (isNaN(date.getTime())) {
      return fallback
    }
    return format(date, formatString)
  } catch (error) {
    console.error(`Erro ao formatar data: ${dateString}`, error)
    return fallback
  }
}

// Aviso do médico "coringa" (422 PLACEHOLDER_DOCTOR_BLOCKED) em vez do toast.
const placeholderBlocked = usePlaceholderDoctorBlocked()
// Qualificação exigida (SPEC §4): 422 → bloqueio (sem liberação manual) ou pré-alocação confirmada.
const qualificationGuard = useQualificationAllocationGuard()

const createDefaultForm = () => ({
  doctor: undefined as string | undefined,
  doctorName: '',
  userSponsorId: null,
  value: 0,
  inCash: false,
  advanceRequested: false,
  advancePaid: false,
  modifiedValue: false,
  observations: [] as Observation[],
  date: '',
  endDate: '',
  startHour: '',
  endHour: '',
  finishedAtUntil: '',
  timezone: '',
  stationType: 'FIXO' as StationTypes,
  histories: [] as HistoryDate[],
  formation: '',
  team: '',
  position: '',
  shift: {
    id: 0,
    name: ''
  },
  sector: null as StationSectorRef | null,
  specialty: null as SpecialtySelection | null,
  ourTeam: false
})
const form = reactive(createDefaultForm())

/** Valor gravado no plantão ao abrir o modal: base da semântica de patch (P4). */
const initialSectorSpecialty = ref<{
  sector: StationSectorRef | null
  specialty: SpecialtySelection | null
}>({
  sector: null,
  specialty: null
})

const healthUnitIdForSector = computed<number | null>(() => {
  const id = Number(props.infos.schedule?.healthUnit?.id)
  return Number.isFinite(id) && id > 0 ? id : null
})

/** E-06 / Q31: estado explícito para o PUT de plantão sem médico (null = fluxo com médico). */
const coverageInput = computed(() => buildUpdateCoverageInput(props.infos))
const isUnidentifiedStation = computed(
  () => props.infos.coverageStatus === CoverageStatus.UNIDENTIFIED
)

// A11: o caminho de vaga aberta (lote com 1 id, campos travados) só existe com a flag ligada;
// desligada, a vaga aberta segue a regra da base (PUT). Ocupado sem identificação NUNCA
// entra aqui: com a flag ligada ou desligada, edita horário/valor pelo PUT (E-06).
const isOpenStation = computed(
  () =>
    !!flags.enabledDashboardV2 && !props.infos.doctorStation?.doctor && !isUnidentifiedStation.value
)
const OPEN_STATION_NOTICE = 'Vaga aberta: aqui só setor e especialidade são salvos.'

/** Horário e valor não editáveis: vaga aberta (H6) ou perna travada (backend #3055). */
const areScheduleFieldsLocked = computed(() => isOpenStation.value || isLegLocked.value)

/** Só os campos que o usuário alterou; ausente = mantém, null = limpa (spec §4.2). */
const sectorSpecialtyPatch = computed(() =>
  buildSectorSpecialtyPatch(initialSectorSpecialty.value, {
    sector: form.sector,
    specialty: form.specialty
  })
)

const describeSectorSpecialty = (
  sector: StationSectorRef | null,
  specialty: SpecialtySelection | null
) => `${catalogDisplayName(sector, '— sem setor')} · ${specialty?.name ?? '— sem especialidade'}`

/**
 * F3 (nota do BE): a resposta do PUT não traz setor/especialidade atualizados. Com o modal
 * ainda aberto depois de salvar, a classificação exibida (e a base do patch) vem de um
 * `GET /stations/:id` fresco.
 */
async function reloadSectorSpecialtyFromServer(): Promise<void> {
  try {
    const fresh = await NetworkService.station.getStationDetails(props.infos.stationId)
    // Sem corpo: mesmo tratamento da falha (mantém a escolha do usuário).
    if (!fresh) return
    form.sector = fresh.sector ?? null
    form.specialty = specialtyRefToSelection(fresh.specialty)
    initialSectorSpecialty.value = { sector: form.sector, specialty: form.specialty }
  } catch {
    // fallback intencional: a gravação já deu certo e foi avisada; sem o GET o modal
    // continua com o que o usuário escolheu (o detalhe relê o plantão ao reabrir).
  }
}

/** H6: prévia antes → depois, só quando algo mudou. */
const sectorSpecialtyPreview = computed(() =>
  hasSectorSpecialtyChanges(sectorSpecialtyPatch.value)
    ? `Antes: ${describeSectorSpecialty(
        initialSectorSpecialty.value.sector,
        initialSectorSpecialty.value.specialty
      )} → Depois: ${describeSectorSpecialty(form.sector, form.specialty)}`
    : null
)

const {
  violations: consecutiveHoursViolations,
  hasViolations: hasConsecutiveHoursViolations,
  runCheck: runConsecutiveHoursCheck,
  applyErrorViolations: applyConsecutiveHoursError,
  clearViolations: clearConsecutiveHoursViolations
} = useConsecutiveHoursCheck()

// Conflito de agenda: aviso a posteriori (o backend devolve junto do plantao ja gravado).
// Nao bloqueia o submit — so segura o modal aberto para o usuario ler.
const {
  alerts: overlapAlerts,
  hasAlerts: hasOverlapAlerts,
  applyFromResponse: applyOverlapAlerts
} = useStationOverlapAlerts()

watch(
  () => [form.doctor, form.team, form.date, form.endDate, form.startHour, form.endHour],
  () => clearConsecutiveHoursViolations()
)

function buildConsecutiveHoursCandidates(
  doctorId: number,
  startDate: Date,
  endDate: Date
): ConsecutiveHoursCheckCandidate[] {
  const scheduleId = Number(props.infos.scheduleId)
  if (!Number.isFinite(scheduleId)) return []
  const doctorStationId = props.infos.doctorStation?.id
  return [
    {
      scheduleId,
      doctorId,
      stationId: props.infos.stationId,
      startHour: startDate.toISOString(),
      endHour: endDate.toISOString(),
      ourTeam: form.team === 'Interno',
      timezone: form.timezone,
      // A própria alocação sendo editada não pode contar contra si mesma.
      excludeDoctorStationIds: doctorStationId ? [doctorStationId] : undefined
    }
  ]
}

const formErrors = ref<{
  userSponsorId?: string
}>({})
const isSavingLoading = ref(false)
const isExcludingLoading = ref(false)
const showModalDelete = ref(false)
const activeIndex = ref(0)
const hasPendingChecks = ref(false)
const isAllocatable = ref(true)

const {
  values: reasonValues,
  showErrors: showReasonErrors,
  fields: reasonFields,
  reset: resetReasons,
  validate: validateReasons,
  build: buildReasons
} = useChangeReasonForm(() => [
  {
    kind: 'deletion',
    title: CHANGE_REASON_TITLES.deletion(1),
    label: CHANGE_REASON_LABELS.deletion,
    options: DELETION_REASON_OPTIONS
  }
])
const reasonScopeNote = ''

/**
 * acceptance-terms (D8/D54d, CA-11): salvar ou excluir cancela o convite/troca pendente do
 * plantão (cascata D54 no backend). Com a flag ligada, o escalista é avisado antes; desligada,
 * `fetchPendingNotice` devolve `null` sem chamada e o modal é o de hoje.
 */
const { fetchNotice: fetchPendingNotice } = useStationPendingNotice()
/** Aviso da gravação aguardando confirmação; `null` = fechado. */
const pendingWarning = ref<string | null>(null)
/** Aviso dentro da confirmação de exclusão. */
const deletePendingNotice = ref<string | null>(null)
const isCheckingDeletePending = ref(false)
// Dono do aviso da exclusão: a resposta de uma abertura antiga não sobrescreve a atual.
let deletePendingSeq = 0

/** Abre o aviso quando há pendência (ou a consulta falhou); `true` = a gravação espera o sim. */
async function askPendingConfirmation(): Promise<boolean> {
  const notice = await fetchPendingNotice(props.infos.stationId)
  if (!notice) return false
  pendingWarning.value = notice
  return true
}

function confirmPendingWarning() {
  pendingWarning.value = null
  void submitStation(false, true)
}

function cancelPendingWarning() {
  pendingWarning.value = null
}

async function refreshDeletePendingNotice(open: boolean) {
  const seq = ++deletePendingSeq
  deletePendingNotice.value = null
  isCheckingDeletePending.value = open
  if (!open) return
  const notice = await fetchPendingNotice(props.infos.stationId)
  if (seq !== deletePendingSeq) return
  deletePendingNotice.value = notice
  isCheckingDeletePending.value = false
}

// Toda abertura da confirmação começa sem motivo (e com o aviso de pendência atualizado).
watch(showModalDelete, (open) => {
  if (open) resetReasons()
  void refreshDeletePendingNotice(open)
})

/**
 * Seção "Tentativas de check": fica recolhida por padrão e só monta o painel no
 * clique. O `source` é um computed (e não um objeto literal no template) porque
 * o `CheckAttemptsPanel` observa a prop com `deep: true` — um literal recriado a
 * cada re-render dispararia uma busca nova a cada digitação no formulário.
 */
const showCheckAttempts = ref(false)
const checkAttemptsSource = computed(() => ({ stationId: Number(props.infos.stationId) }))

watch(
  () => props.infos?.stationId,
  () => {
    showCheckAttempts.value = false
  }
)

const toast = useToast()
const { notifyError } = useErrorToast()
const stationStore = useStationStore()

const mapTimezones = {
  '-02:00': 'America/Noronha',
  '-03:00': 'America/Sao_Paulo',
  '-04:00': 'America/Manaus',
  '-05:00': 'America/Rio_Branco'
}
const scheduleProportionalityRule = ref<string | null>('N/A')

// eslint-disable-next-line complexity
onMounted(() => {
  if (props.showModal && props.infos) {
    // Sem `await`: a trava é uma restrição, não um dado do formulário. Deixar o
    // modal montar na hora e o botão desabilitar quando a resposta chega é
    // melhor que um skeleton — e o backend recusa de todo modo.
    refreshPostStationLock()
    // Mesma razão: restrição que chega depois; sem resposta, segue destravado.
    void refreshLegLock()

    // Mesma razão do `refreshPostStationLock` acima: a aba de checks monta no
    // comportamento de hoje (`false`) e só entra em somente leitura quando a
    // resposta chega. A ação nunca lança — backend antigo cai no default seguro.
    void dsAttendanceStore.fetchRegistrationSettings(cardScheduleId.value)

    scheduleProportionalityRule.value = getProportionalityRuleTitle(
      props.infos.schedule?.proportionalityRule
    )

    const tz = props.infos.timezone
    const mappedTz = mapTimezones[tz as keyof typeof mapTimezones] || tz

    const incomingDoctorId = props.infos.doctorStation?.doctor?.id
    const incomingMainCrm = props.infos.doctorStation?.doctor?.mainCrm
    isAllocatable.value = !!incomingDoctorId && !!incomingMainCrm

    if (incomingDoctorId && !isAllocatable.value) {
      toast.warning('Vaga sem médico válido alocado. Selecione um médico para salvar.')
    }

    Object.assign(form, {
      doctor: isAllocatable.value ? String(incomingDoctorId) : undefined,
      doctorName: props.infos.doctorStation?.doctor?.mainCrm?.name,
      value: props.infos.value,
      inCash: !!props.infos.inCash,
      advancePaid: !!props.infos.advancePaid,
      advanceRequested: !!props.infos.advanceRequested,
      modifiedValue: !!props.infos.modifiedValue,
      date: safeFormat(props.infos.date.toString(), 'dd/MM/yyyy', mappedTz, ''),
      endDate: safeFormat(props.infos.endHour?.toString(), 'dd/MM/yyyy', mappedTz, ''),
      startHour: safeFormat(props.infos.startHour?.toString(), 'HH:mm', mappedTz, ''),
      endHour: safeFormat(props.infos.endHour?.toString(), 'HH:mm', mappedTz, ''),
      finishedAtUntil: safeFormat(props.infos.endHour?.toString(), 'dd/MM/yyyy', mappedTz, ''),
      timezone: tz,
      stationType: props.infos.stationType,
      histories: props.infos.checks ?? [],
      ourTeam: !!props.infos.ourTeam,
      formation: (props.infos as any).doctorStation?.dsPaymentDoctorFormation?.id || '',
      team: props.infos.ourTeam ? 'Interno' : 'Externo',
      position: props.infos.position?.name || '',
      shift: (props.infos as any).shift || '',
      userSponsorId: props.infos.userSponsor?.id,
      sector: props.infos.sector ?? null,
      specialty: specialtyRefToSelection(props.infos.specialty)
    })
    initialSectorSpecialty.value = { sector: form.sector, specialty: form.specialty }
    const matchingCardIndex = dutyFeeCards.value.findIndex((c) => c.value === form.value)
    activeIndex.value = matchingCardIndex !== -1 ? matchingCardIndex : 0
  }
})

const dutyFeeCards = computed(() => {
  const policies = props.infos.schedule?.paymentPolicies
  if (!policies || policies.length === 0) return []
  return policies[0].rules.map((rule: any) => ({
    formation: rule.ruleFormations?.[0]?.name || null,
    value: parseFloat(rule.hourValue),
    repassMode: rule.paymentTiming
  }))
})

const isSaveDisabled = computed(() => {
  // Vaga aberta: o PUT exige médico, então só dá para salvar setor/especialidade (H6).
  if (isOpenStation.value) {
    return isPostStationLocked.value || !hasSectorSpecialtyChanges(sectorSpecialtyPatch.value)
  }
  return (
    // Sem médico só salva quando o BE informa o estado da cobertura (OPEN/UNIDENTIFIED).
    (!isAllocatable.value && !coverageInput.value) ||
    hasConsecutiveHoursViolations.value ||
    isPostStationLocked.value
  )
})

// `preAllocate`: reenvio confirmado depois do 422 QUALIFICATION_PRE_ALLOCATION_REQUIRED (D24).
// `pendingConfirmed`: o escalista já confirmou o aviso de convite/troca pendente (D8/D54d).
// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
async function submitStation(preAllocate: boolean, pendingConfirmed = false) {
  formErrors.value = {}

  // pode salvar mesmo com o modo de edição aberto nos checks
  // if (hasPendingChecks.value) {
  //   return toast.error(
  //     'Existe Rascunho de registro de presença. Salve ou descarte antes de confirmar a edição.'
  //   )
  // }

  if (hasDutyRegisterPendingChanges.value) {
    return toast.error(
      'Você tem alterações pendentes no "Registro de plantão". Salve o rascunho antes de continuar.'
    )
  }

  // H6: vaga aberta não passa pelo PUT (que exige médico); setor/especialidade vão
  // pelo endpoint de lote com 1 id (A8).
  if (isOpenStation.value) {
    if (!hasSectorSpecialtyChanges(sectorSpecialtyPatch.value)) return
    try {
      isSavingLoading.value = true
      // O lote de classificação também cancela o convite pendente do plantão alterado.
      if (!pendingConfirmed && (await askPendingConfirmation())) return
      await NetworkService.station.bulkSectorSpecialty(
        patchToBulkRequest([props.infos.stationId], sectorSpecialtyPatch.value)
      )
      toast.success('Setor e especialidade salvos')
      await stationStore.getListStations()
      emit('saved')
    } catch (err: unknown) {
      notifyError(err, 'Não foi possível salvar setor e especialidade')
    } finally {
      isSavingLoading.value = false
    }
    return
  }

  if (!form.stationType) return toast.error('Selecione o tipo de periodicidade do plantão.')
  if (!form.date || !form.startHour || !form.endHour || !form.timezone)
    return toast.error('Preencha a data, horário e fuso horário do plantão.')
  if (!validationState.value.isValid)
    return toast.error(`Erro no preenchimentos das datas: ${validationState.value.message || ''}`)

  if (props.infos.schedule?.requiresDoctorPaymentFormation && !form.formation && !!form.doctor) {
    return toast.error('Informe a formação do médico.')
  }

  if (!form.team) return toast.error('Informe o time do médico.')
  if (!form.userSponsorId && !!form.doctor) {
    formErrors.value = {
      ...formErrors.value,
      userSponsorId: 'Campo Médico escalado por é obrigatório'
    }
    return toast.error('Informe por quem o médico foi alocado.')
  }

  try {
    isSavingLoading.value = true

    const { date, startHour, endHour, timezone, endDate: endDateSelected } = form

    const { startDate, endDate } = getStartDateAndEndDate({
      date,
      startHour,
      endHour,
      timezone,
      endDateSelected
    })

    const originChecks = getHistoriesRequest(form.histories, startDate, endDate)

    // Plantão sem médico com estado informado (OPEN/UNIDENTIFIED): nunca manda médico.
    const coverage = coverageInput.value
    const formDoctorId = !coverage && form.doctor ? Number(form.doctor) : undefined
    const resolvedDoctorId = Number.isFinite(formDoctorId) ? formDoctorId : undefined

    if (!resolvedDoctorId && !coverage) {
      toast.error(
        'Esta vaga possui médico inválido. Para alterar, acesse o gerenciamento de alocações.'
      )
      return
    }

    if (resolvedDoctorId) {
      const canProceed = await runConsecutiveHoursCheck(
        buildConsecutiveHoursCandidates(resolvedDoctorId, startDate, endDate)
      )
      if (!canProceed) {
        toast.error('Limite de horas consecutivas excedido. Veja os detalhes no formulário.')
        return
      }
    }

    // Último passo antes do PUT: só avisa o que de fato vai ser gravado.
    if (!pendingConfirmed && (await askPendingConfirmation())) return

    // O backend recusa lançamento manual de check em UNIDENTIFIED: `histories` é omitido
    // (ausente = mantém), pela mesma razão do bloqueio global abaixo.
    const omitHistories = manualCheckBlockEnabled.value || isUnidentifiedStation.value

    /**
     * `histories` é OMITIDO — e não enviado como `[]` — quando a aba de checks
     * está somente leitura. O `PUT` apaga todo check ausente de `histories[]`, e
     * um array vazio passa pelo guard do backend: mandar `[]` daqui apagaria os
     * checks do plantão sem ninguém ter clicado em nada. O backend com a flag
     * ligada também ignora o campo — são duas travas para o mesmo buraco.
     */
    const payload: Omit<UpdateStationRequest, 'histories'> & {
      histories?: HistoryDateRequest[]
    } = {
      doctorId: resolvedDoctorId,
      userSponsorId: coverage ? null : form.userSponsorId,
      formation: !coverage && form.formation ? Number(form.formation) : undefined,
      position: Number(form.position),
      team: form.team,
      advancePaid: form.advancePaid,
      advanceRequested: form.advanceRequested,
      stationType: form.stationType as StationTypes,
      config: { date: startDate, startHour: startDate, endHour: endDate, timezone: form.timezone },
      observations: form.observations,
      value: Number(form.value),
      inCash: form.inCash,
      ourTeam: isUnidentifiedStation.value ? false : form.team === 'Interno',
      ...(omitHistories ? {} : { histories: originChecks || [] }),
      modifiedValue: form.modifiedValue,
      ...(flags.enabledDashboardV2 ? sectorSpecialtyPatch.value : {}),
      ...(coverage ?? {}),
      ...(preAllocate ? { preAllocate: true } : {})
    }

    const formData = new FormData()
    formData.append('data', JSON.stringify(payload))

    // Mesma razão: sem `histories` no payload, nenhum `historyEvidence[i][]`
    // deve viajar junto — não há linha para ancorá-lo.
    if (form.histories && !omitHistories) {
      form.histories.forEach((history, index) => {
        // Agora 'history' é do tipo HistoryDate (que TEM 'evidence')
        if (history.evidence && Array.isArray(history.evidence)) {
          history.evidence.forEach((file) => {
            if (file instanceof File) {
              formData.append(`historyEvidence[${index}][]`, file)
            }
          })
        }
      })
    }

    const updated = await NetworkService.station.update(
      props.infos.stationId,
      formData,
      Number(props.infos.scheduleId)
    )
    toast.success('Plantão editado com sucesso')
    await stationStore.getListStations()

    // A edicao ja esta gravada; se o backend acusou sobreposicao, o modal fica aberto
    // exibindo o aviso ambar em vez de fechar e engolir a informacao.
    if (applyOverlapAlerts(updated)) {
      if (flags.enabledDashboardV2) await reloadSectorSpecialtyFromServer()
      return
    }

    emit('saved')
  } catch (err: unknown) {
    // Guards F10: o service normaliza via handleError (ServiceError) e os guards
    // fazem unwrap do axios original — os ramos específicos ficam como estão.
    if (applyConsecutiveHoursError(err)) {
      // eslint-disable-next-line no-restricted-syntax -- toast fixo + abre o formulário de violações; escolha de UX
      toast.error('Limite de horas consecutivas excedido. Veja os detalhes no formulário.')
      return
    }

    if (isStationDurationExceeded(err)) {
      // Frase pronta do backend (cita o limite real); o texto fixo é só o fallback.
      toast.error(
        getStationDurationExceededMessage(
          err,
          'A duração do plantão não pode ser superior a 24 horas.'
        )
      )
      return
    }

    // A trava do Pós-Plantão pode aparecer aqui mesmo com o botão habilitado: a
    // varredura pode aprovar o plantão enquanto o modal está aberto. O backend
    // manda uma frase pronta em pt-BR — repassar em vez de reescrever.
    if (applyPostStationApprovedError(err)) {
      toast.error(getPostStationApprovedMessage(err))
      return
    }

    // Qualificação exigida: o 422 só vem com médico novo, então o nome do médico anterior não serve.
    if (
      qualificationGuard.handleError(err, {
        doctorName: null,
        // O reenvio segue uma gravação já confirmada (ou sem pendência): não pergunta de novo.
        retryWithPreAllocate: () => submitStation(true, true)
      })
    ) {
      return
    }

    if (placeholderBlocked.handleError(err, props.infos.doctorStation?.doctor?.mainCrm?.name)) {
      return
    }

    const coverageMessage = getCoverageErrorMessage(err)
    if (coverageMessage) {
      toast.error(coverageMessage)
      return
    }

    if (getApiErrorStatus(err) === 400 && isStationCheckInLocked(err)) {
      // Frase pronta do backend (por contexto); o texto fixo é só o fallback.
      toast.error(
        getStationCheckInLockMessage(
          err,
          'Este plantão já possui check-in realizado e não pode ser editado.'
        )
      )
    } else {
      // Antes: "Ops, não foi possível processar a requisição" — a mensagem
      // específica do backend (via ServiceError) chega intacta ao usuário.
      notifyError(err, 'Não foi possível editar o plantão')
    }
  } finally {
    isSavingLoading.value = false
  }
}

const handleSubmit = () => submitStation(false)

const handleDeleteStation = async () => {
  if (!validateReasons()) return
  try {
    isExcludingLoading.value = true

    await NetworkService.station.delete(
      props.infos.stationId,
      props.infos.schedule.id,
      buildReasons()
    )
    toast.success('Plantão removido com sucesso')
    await stationStore.getListStations()
    showModalDelete.value = false
  } catch (err: unknown) {
    if (getApiErrorStatus(err) === 400 && isStationCheckInLocked(err)) {
      toast.error(
        getStationCheckInLockMessage(
          err,
          'Este plantão já possui check-in realizado e não pode ser removido.'
        )
      )
    } else if (applyPostStationApprovedError(err)) {
      // Mesmo caso do submit: a aprovação pode chegar com o modal aberto.
      toast.error(getPostStationApprovedMessage(err))
    } else {
      // Antes: "Ops, não foi possível processar a requisição" genérico.
      notifyError(err, 'Não foi possível remover o plantão')
    }
    showModalDelete.value = false
  } finally {
    isExcludingLoading.value = false
    emit('closeModal')
  }
}

const updateChecks = (h: HistoryDate[]) => {
  form.histories = h
}

const hasDutyRegisterPendingChanges = ref(false)

function handleChildChanges(isDirty: boolean) {
  console.log('handleChildChanges called')
  console.log('handleChildChanges called with isDirty:', isDirty)
  hasDutyRegisterPendingChanges.value = isDirty
}

const handleEditModeChanged = (isEditing: boolean) => {
  hasPendingChecks.value = isEditing
}

/**
 * Registro de Permanência (chip 🔗 + linha de vínculo em `doctor.checks.vue`):
 * navegação real, empilhada sobre este modal, para o `StationDetailsModal`
 * (read-only) do plantão vinculado — em vez do evento sem destino de antes.
 */
const linkedStationModal = ref<{ show: boolean; stationId: number | null }>({
  show: false,
  stationId: null
})

function handleNavigateToLinkedStation(stationId: number) {
  linkedStationModal.value = { show: true, stationId }
}

function closeLinkedStationModal() {
  linkedStationModal.value.show = false
  // Zera o stationId para desmontar o StationDetailsModal (pesado) em vez de
  // deixá-lo montado (apenas oculto) atrás deste modal.
  linkedStationModal.value.stationId = null
}

const setActiveIndex = (index: string | number) => {
  if (areScheduleFieldsLocked.value) return
  const i = Number(index)
  if (dutyFeeCards.value[i]) {
    activeIndex.value = i
    form.value = dutyFeeCards.value[i].value
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.showModal) {
    // Modal empilhado (StationDetailsModal do plantão vinculado) está aberto:
    // deixa o Escape dele fechar primeiro, em vez de fechar os dois de uma vez.
    if (linkedStationModal.value.show) return
    // Idem para o aviso do médico coringa e os diálogos de qualificação.
    if (placeholderBlocked.isOpen.value) return
    if (qualificationGuard.blocked.open || qualificationGuard.preAllocation.open) return
    // O aviso de pendência fecha sozinho (desistir), sem levar o "Editar plantão" junto.
    if (pendingWarning.value !== null) return cancelPendingWarning()
    emit('closeModal')
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>
